import {
  Component,
  computed,
  ContentChild,
  effect,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  input,
  numberAttribute,
  OnDestroy,
  Output,
  signal,
  ViewChildren,
} from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { map } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import {
  CrmBoardColumnInterface,
  CrmBoardDatasourceInterface,
  CrmBoardItemClickedEvent,
  CrmBoardItemUpdatedEvent,
  ItemUpdateStrategy,
} from '../interfaces';
import { CrmBoardColumnComponent } from '../column/column.component';
import { CrmBoardColumnHeaderDirective } from '../column/column-header.directive';
import { CrmBoardCardContentDirective } from '../card/card-content.directive';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { DropItemEvent } from '../internal';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { toSignal } from '@angular/core/rxjs-interop';

interface NavItem {
  id: string;
  active: boolean;
}
/**
 * Visibility options for the carousel
 * - `never`: The carousel is never visible
 * - `mobile`: The carousel is visible on mobile devices
 * - `always`: The carousel is always visible
 * - `auto`: The carousel is visible on mobile or when the board width is less than the sum of the column widths
 */
type CarouselVisibility = 'never' | 'mobile' | 'always' | 'auto';

@Component({
  selector: 'crm-board',
  templateUrl: `./board.component.html`,
  styleUrls: ['./board.component.scss'],
  imports: [MatIcon, CrmBoardColumnComponent, DragDropModule, MatProgressBarModule],
})
export class CrmBoardComponent<RowType, FilterType, SortType> implements OnDestroy {
  @Input({ required: true }) datasource?: CrmBoardDatasourceInterface<RowType, FilterType, SortType>;
  readonly columns = input.required<CrmBoardColumnInterface<FilterType, RowType>[]>();
  @Input() itemUpdateStrategy: ItemUpdateStrategy = 'none';
  readonly minColumnWidth = input(0, { transform: numberAttribute });
  readonly carousel = input<CarouselVisibility>('mobile');
  @Output() itemUpdateFailed = new EventEmitter<CrmBoardItemUpdatedEvent<RowType, FilterType>>();
  @Output() itemClicked = new EventEmitter<CrmBoardItemClickedEvent>();

  @ContentChild(CrmBoardColumnHeaderDirective) columnHeader?: CrmBoardColumnHeaderDirective;
  @ContentChild(CrmBoardCardContentDirective) cardContent?: CrmBoardCardContentDirective;
  @ViewChildren(CrmBoardColumnComponent) columnComponents?: CrmBoardColumnComponent<RowType, FilterType, SortType>[];

  protected readonly updateInProgress = signal(false);
  protected readonly columnNavItems = signal<NavItem[]>([]);
  protected readonly showCarousel = computed(() => this.computeShowCarousel());

  private readonly mobileBreakpoint$ = this.breakpointObserver
    .observe([Breakpoints.TabletPortrait, Breakpoints.HandsetPortrait])
    .pipe(map((breakpoints) => breakpoints.matches));
  protected readonly mobileBreakpoint = toSignal(this.mobileBreakpoint$);

  private readonly elementWidth = signal(0);
  private readonly resizeObserver = new ResizeObserver((entries) =>
    this.elementWidth.set(entries[0]?.contentRect.width ?? 0),
  );

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    private readonly elementRef: ElementRef,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.resizeObserver.observe(this.elementRef.nativeElement);
    effect(() => {
      const minColumnWidth = this.minColumnWidth() ?? 0;
      this.document.documentElement.style.setProperty('--min-column-width', `${minColumnWidth}px`);
    });
    effect(() => {
      const columns = this.columns();
      const breadcrumbs: NavItem[] = columns.map((column) => ({ id: column.id, active: false }));
      this.columnNavItems.set(breadcrumbs);
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver.unobserve(this.elementRef.nativeElement);
  }

  protected nextColumn(): void {
    const breadcrumbs = [...this.columnNavItems()].reverse();
    const lastVisibleIndex = breadcrumbs.findIndex((breadcrumb) => breadcrumb.active);
    if (lastVisibleIndex > 0) {
      const previousColumnId = breadcrumbs[lastVisibleIndex - 1].id;
      this.scrollColumnIntoView(previousColumnId);
    }
  }

  protected previousColumn(): void {
    const breadcrumbs = [...this.columnNavItems()];
    const firstVisibleIndex = breadcrumbs.findIndex((breadcrumb) => breadcrumb.active);
    if (firstVisibleIndex > 0) {
      const previousColumnId = breadcrumbs[firstVisibleIndex - 1].id;
      this.scrollColumnIntoView(previousColumnId);
    }
  }

  protected scrollColumnIntoView(columnId: string): void {
    this.document.querySelector(`[data-column-id="${columnId}"]`)?.scrollIntoView({ behavior: 'smooth' });
  }

  protected onColumnVisible(columnId: string): void {
    this.columnNavItems.update((breadcrumbs) => {
      const item = breadcrumbs.find((breadcrumb) => breadcrumb.id === columnId);
      if (item) {
        item.active = true;
      }
      return breadcrumbs;
    });
  }

  protected onColumnHidden(columnId: string): void {
    this.columnNavItems.update((breadcrumbs) => {
      const item = breadcrumbs.find((breadcrumb) => breadcrumb.id === columnId);
      if (item) {
        item.active = false;
      }
      return breadcrumbs;
    });
  }

  protected onItemDropped(event: DropItemEvent<RowType>): void {
    const columns = this.columns();
    const fromColumn = columns.find((c) => c.id === event.from);
    const toColumn = columns.find((c) => c.id === event.to);
    if (!fromColumn || !toColumn) {
      throw new Error('Invalid drop event');
    }
    if (toColumn.mutation === undefined) {
      return;
    }
    const mutation = toColumn.mutation(event.item);
    const updateEvent: CrmBoardItemUpdatedEvent<RowType, FilterType> = {
      item: event.item,
      column: toColumn,
      from: fromColumn,
    };
    this.applyPreUpdateStrategy(updateEvent);
    this.updateInProgress.set(true);
    mutation
      .then(() => {
        this.applyPostUpdateStrategy(updateEvent);
      })
      .catch(() => {
        this.rollbackPreUpdateStrategy(updateEvent);
        this.itemUpdateFailed.emit(updateEvent);
      })
      .finally(() => this.updateInProgress.set(false));
  }

  private applyPreUpdateStrategy(updateEvent: CrmBoardItemUpdatedEvent<RowType, FilterType>): void {
    if (this.itemUpdateStrategy === 'none') {
      return;
    }
    if (this.itemUpdateStrategy === 'cache') {
      const fromColumn = this.columnComponents?.find((c) => c.column?.id === updateEvent.from.id);
      const toColumn = this.columnComponents?.find((c) => c.column?.id === updateEvent.column.id);
      fromColumn?.cacheMutation('remove', updateEvent.item);
      toColumn?.cacheMutation('add', updateEvent.item);
    }
  }

  private applyPostUpdateStrategy(updateEvent: CrmBoardItemUpdatedEvent<RowType, FilterType>): void {
    if (this.itemUpdateStrategy === 'none') {
      return;
    }
    if (this.itemUpdateStrategy === 'refresh') {
      const fromColumn = this.columnComponents?.find((c) => c.column?.id === updateEvent.from.id);
      const toColumn = this.columnComponents?.find((c) => c.column?.id === updateEvent.column.id);
      fromColumn?.reset();
      toColumn?.reset();
    }
  }

  private rollbackPreUpdateStrategy(updateEvent: CrmBoardItemUpdatedEvent<RowType, FilterType>): void {
    if (this.itemUpdateStrategy === 'none') {
      return;
    }
    if (this.itemUpdateStrategy === 'cache') {
      const fromColumn = this.columnComponents?.find((c) => c.column?.id === updateEvent.from.id);
      const toColumn = this.columnComponents?.find((c) => c.column?.id === updateEvent.column.id);
      fromColumn?.cacheMutation('add', updateEvent.item);
      toColumn?.cacheMutation('remove', updateEvent.item);
    }
  }

  private computeShowCarousel(): boolean {
    const mobileBreakpoint = this.mobileBreakpoint();
    const minColumnWidth = this.minColumnWidth();
    const boardWidth = this.elementWidth();
    const columnCount = this.columns()?.length ?? 0;
    const carouselVisibility = this.carousel();

    switch (carouselVisibility) {
      case 'never':
        return false;
      case 'always':
        return true;
      case 'mobile':
        return !!mobileBreakpoint;
      case 'auto':
        return minColumnWidth > 0 && boardWidth < minColumnWidth * columnCount;
      default:
        return false;
    }
  }

  protected onItemClicked(itemClickEvent: CrmBoardItemClickedEvent): void {
    this.itemClicked.emit(itemClickEvent);
  }
}
